import {FormArray, FormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {CustomFieldType, IntakeFormFields, IntakeFormSections} from '@ee/common/enums';
import {CustomField, CustomFieldOption, IntakeForm, IntakeOption} from '@ee/common/models';
import {ALL_EVICTION_REASONS} from '@ee/common/constants';
import {ChoiceOptionsValidator} from '@ee/common/validators';
import {normalizeEvictionReasons, storedEvictionReasons} from '@ee/common/utils';
import {defaultValueFor} from './custom-field.form';

/** The list control: every selected reason, the source of truth (EE-392). */
export const EVICTION_REASONS_CONTROL = 'eviction_reasons';

/** The reason a fresh intake form (and a "Reset to defaults", EE-392) defaults to. */
export const UNPAID_RENT = 'Unpaid Rent';

function reasonOption(options: Map<string, IntakeOption> | undefined | null): IntakeOption | undefined {
  // Intake options arrive from JSON as a plain object despite the Map type, so index, don't .get().
  return options?.[IntakeFormFields.EVICTION_REASON] as IntakeOption | undefined;
}

/**
 * The server-generated Reason_Field: the `eviction_reason` built-in of the intake form's EVICTION_REASON
 * section, typed RADIO or MULTI_SELECT and carrying the firm's choices, default and separator
 * (`IntakeFormUtils.generateBuiltInField`, Req 4.2). Undefined when the form has no such section.
 */
export function evictionReasonFieldOf(intakeForm: IntakeForm | undefined | null): CustomField | undefined {
  return intakeForm?.sections?.[IntakeFormSections.EVICTION_REASON]?.built_in_fields
    ?.find(f => f.field === IntakeFormFields.EVICTION_REASON);
}

/**
 * The Reason_Field for a host that has only the options map (the builder/customizer previews). A mirror
 * of the server's `EvictionReasonOptions.applyTo`, kept to that one derivation: legacy null / `SELECT` ⇒
 * RADIO; the firm's definitions, else its legacy `options` (label = value), else every canonical reason;
 * the stored default reshaped for the mode and limited to the choices, with the "Unpaid Rent" fallback
 * only while no definitions are stored (ruling R11).
 */
export function evictionReasonFieldFromOptions(options: Map<string, IntakeOption> | undefined | null): CustomField {
  const option = reasonOption(options);
  const type = option?.type === String(CustomFieldType.MULTI_SELECT) ? CustomFieldType.MULTI_SELECT : CustomFieldType.RADIO;
  const definitions: CustomFieldOption[] = option?.option_definitions?.length
    ? option.option_definitions.map(d => ({...d}))
    : (option?.options?.length ? option.options : ALL_EVICTION_REASONS).map(value => ({value, label: value}));
  const values = definitions.map(d => d.value);
  return {
    field: IntakeFormFields.EVICTION_REASON,
    label: 'Eviction Reason',
    type,
    option_definitions: definitions,
    options: values,
    default_value: effectiveDefault(option, type, values),
    render_separator: option?.render_separator ?? null,
    choice_layout: option?.choice_layout ?? null,
    required: option?.required ?? true,
    hide: option?.show === false,
    hide_from_intake: option?.hide_from_intake ?? false,
    built_in_field: true
  };
}

/**
 * The server's Reason_Field when the host has one typed RADIO or MULTI_SELECT (preferred), else one derived
 * from the options map. A pre-EE-392 backend (client deployed first, or a rollback) still generates the
 * built-in typed `SELECT` with a blank default; trusting it would render a Radio Group with no "Unpaid
 * Rent" default, so any other type falls back to the options-map derivation.
 */
export function resolveEvictionReasonField(
  reasonField: CustomField | undefined | null,
  options: Map<string, IntakeOption> | undefined | null
): CustomField {
  const usable = reasonField?.type === CustomFieldType.RADIO || reasonField?.type === CustomFieldType.MULTI_SELECT;
  return usable ? reasonField : evictionReasonFieldFromOptions(options);
}

/**
 * Server `convertDefault`: RADIO keeps one value (a list's first), MULTI_SELECT wraps a value; blank ⇒ none.
 * Also what the builder's "Display as" switch applies to the authored default (Req 2.3).
 */
export function convertEvictionReasonDefault(value: unknown, type: CustomFieldType): string | string[] | null {
  if (type === CustomFieldType.MULTI_SELECT) {
    if (typeof value === 'string') {
      return value.trim() ? [value] : null;
    }
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : null;
  }
  const single = Array.isArray(value) ? value[0] : value;
  return typeof single === 'string' && single.trim() ? single : null;
}

/** Server `effectiveDefault` (ruling R11). */
function effectiveDefault(option: IntakeOption | undefined, type: CustomFieldType, values: string[]): string | string[] | null {
  const legacy = !option?.option_definitions?.length;
  const unpaidRent = values.includes(UNPAID_RENT)
    ? (type === CustomFieldType.MULTI_SELECT ? [UNPAID_RENT] : UNPAID_RENT)
    : null;
  const converted = convertEvictionReasonDefault(option?.default_value, type);
  if (converted === null) {
    return legacy ? unpaidRent : null;
  }
  if (Array.isArray(converted)) {
    return converted.filter(v => values.includes(v));
  }
  return values.includes(converted) ? converted : (legacy ? unpaidRent : null);
}

/**
 * Whether the firm's authored reason choices would be rejected on save: the same rules the shared option
 * editor shows per row (`ChoiceOptionsValidator` — non-blank label, no duplicate label/value, no `;`).
 * Read from the options map rather than the editor so the builder's save stays blocked after the editor
 * panel is closed. A firm with no stored definitions (legacy `options` only) has nothing authored to check.
 */
export function evictionReasonOptionsInvalid(options: Map<string, IntakeOption> | undefined | null): boolean {
  const definitions = reasonOption(options)?.option_definitions;
  if (!definitions) {
    return false;
  }
  const rows = new FormArray(definitions.map(d => new FormControl({...d})));
  return ChoiceOptionsValidator.createValidator()(rows) !== null;
}

/**
 * Whether the reason renders as checkboxes (MULTI_SELECT) rather than a Radio Group: the Reason_Field's
 * type, forced to checkboxes when `forceMultiSelect` says so. The one place both the tenant section and
 * {@link wireEvictionReasons}'s caller decide, so the rendered control and the validator/mirror it wires
 * never disagree.
 *
 * `forceMultiSelect` is {@link storedCaseNeedsCheckboxes} of the stored case the form starts from — the
 * edit-details dialog's case (Req 4.7) or a cloned case — so a Radio-Group firm never shows one radio
 * while a hidden second reason is saved. A fresh intake always follows the firm's mode.
 */
export function effectiveEvictionReasonMultiSelect(
  reasonField: CustomField | undefined | null,
  forceMultiSelect = false
): boolean {
  return reasonField?.type === CustomFieldType.MULTI_SELECT || forceMultiSelect;
}

/**
 * Whether a stored case must be shown with checkboxes whatever the firm's Display_Mode: it already holds
 * more than one reason, which a Radio Group cannot show, so a save would silently drop or hide the rest.
 */
export function storedCaseNeedsCheckboxes(
  eviction: {eviction_reason?: unknown; eviction_reasons?: unknown} | null | undefined
): boolean {
  return storedEvictionReasons(eviction).length > 1;
}

/** Whether a reason must be selected: shown and required per the firm's options (as `processForm` reads them). */
export function evictionReasonRequired(options: Map<string, IntakeOption> | undefined | null): boolean {
  const option = reasonOption(options);
  return !!(option?.show && option?.required);
}

/**
 * Whether the legacy tenant step actually asks the reason.
 *
 * The template renders it only when `options.eviction_reason.show` is strictly true, so a loaded form
 * with no reason entry does not ask it either. No options at all counts as shown: a host that never
 * loaded an intake form still submits whatever the control holds.
 */
export function evictionReasonShown(options: Map<string, IntakeOption> | undefined | null): boolean {
  return !options || reasonOption(options)?.show === true;
}

/** Court-doc tokens such as `#reason_unpaid_rent_check#` and `#reason_squatter_check#`. */
const REASON_CHECK_TOKEN = /^reason_.+_check$/;

/**
 * A reason the filer was never asked.
 *
 * The control still holds the firm's default — Unpaid Rent, when nothing else is configured — and a
 * hidden control stays enabled, so submit would save that default and the generated documents would
 * check it (ISS-163). Same as every other hidden field: the value is left out of the filing. Check
 * tokens already on the payload are dropped too; they are how a template ticks a reason box. The Other
 * description (`reason`) is a different field and is left alone.
 */
export function omitUnaskedEvictionReason<T extends object>(details: T): T {
  const next = {...details, eviction_reason: null, eviction_reasons: [] as string[]} as T & {custom_fields?: unknown};
  stripReasonChecks(next);
  if (isPlainRecord(next.custom_fields)) {
    const custom = {...next.custom_fields};
    stripReasonChecks(custom);
    next.custom_fields = custom;
  }
  return next;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function stripReasonChecks(record: object): void {
  Object.keys(record).forEach(key => {
    if (REASON_CHECK_TOKEN.test(key)) {
      delete (record as Record<string, unknown>)[key];
    }
  });
}

/**
 * The reasons a NEW intake starts with: the Reason_Field's `default_value`, read the way every custom-field
 * default is ({@link defaultValueFor}), as a list — a RADIO default `"X"` is `["X"]`, no default is `[]`.
 * Only for control creation; never applied to a stored case.
 */
export function initialEvictionReasons(reasonField: CustomField): string[] {
  const value = defaultValueFor(reasonField);
  const reasons = normalizeEvictionReasons(Array.isArray(value) ? value : [value]);
  return reasonField?.type === CustomFieldType.MULTI_SELECT ? reasons : reasons.slice(0, 1);
}

/**
 * Wires the reason controls of a tenant form: keeps `eviction_reason` equal to `eviction_reasons[0]`
 * whichever of the two was written, and puts the required validator on the control that is rendered —
 * `eviction_reasons` for checkboxes, `eviction_reason` for a Radio Group.
 * The Other description (`reason`) is deliberately untouched: it keeps its own show/required options in both
 * modes, since firms use it to detail any reason, not only "Other" (ruling R10).
 *
 * Both directions are guarded so a patch of a stored case — which carries both fields, in either key
 * order — never truncates the list to the scalar: the scalar only replaces the list when it disagrees
 * with the list's first entry, i.e. when the Radio Group changed it.
 *
 * Call AFTER `processForm`, which attaches validators by control name and so would put the reason's
 * required validator on `eviction_reason` in both modes.
 *
 * `multiSelect` is what {@link effectiveEvictionReasonMultiSelect} returned for rendering, so the
 * validator always ends up on the control that is actually on screen.
 */
export function wireEvictionReasons(
  form: UntypedFormGroup,
  options: Map<string, IntakeOption> | undefined | null,
  multiSelect: boolean
): void {
  const single = form.get(IntakeFormFields.EVICTION_REASON);
  const list = form.get(EVICTION_REASONS_CONTROL);
  if (!single || !list) {
    return;
  }
  const required = evictionReasonRequired(options) ? [Validators.required] : [];
  if (multiSelect) {
    single.clearValidators();
    list.setValidators(required);
  } else {
    list.clearValidators();
    single.setValidators(required);
  }
  single.updateValueAndValidity({emitEvent: false});
  list.updateValueAndValidity({emitEvent: false});

  // Form-lifetime subscriptions on the form's own controls: they die with the form, so no teardown.
  list.valueChanges.subscribe(value => {
    const first = normalizeEvictionReasons(value)[0] ?? null;
    if (single.value !== first) {
      single.setValue(first, {emitEvent: false});
    }
  });
  single.valueChanges.subscribe(value => {
    const reasons = normalizeEvictionReasons(list.value);
    if ((value || null) !== (reasons[0] ?? null)) {
      list.setValue(value ? [value] : [], {emitEvent: false});
    }
  });
}
